import { createSlice } from "@reduxjs/toolkit";
import { TimeZoneAbbreviations } from "../../utils/parse-date-string/tzData";
import UserRole from "@lxp-app/utils/roles";

export interface IS {
  profile: ProfileTS;
  complyToLatestTOS: boolean;
  tosVersionId: null | string;
  tosAcceptanceDate: null | string;
  visitedOnboarding: boolean;
  org: string;
  token: string | null;
  firstTimeLogin: boolean;
  orgRoutes: Record<string, any>;
}

interface ProfileTS {
  billing: BillingTs;
  email: string;
  employeeNumber: string;
  enabled: boolean;
  employment: employmentTS;
  goalInformation: GoalTs;
  hasAssessmentRecOrgs: boolean;
  isHitachiOrg?: boolean;
  isTMobileOrg?: boolean;
  id: string;
  isManager: boolean;
  isSuperAdmin: boolean;
  location: locationTS;
  name: nameTS;
  onboarding: boolean;
  org: string;
  preferences: preferenceTS;
  profileImageUrl: string;
  project: projectTS;
  role: UserRole[];
  skillTag: Array<skillTypeTs>;
  subscriberIdNovu: string;
  summary: string;
  supervisor: string;
  totalExperience: any;
  unAuthorized: boolean;
  reportees: string[];
}

interface projectTS {
  allocationEndDate: string;
  allocationStartDate: string;
  projectManager: string;
  projectName: string;
  projectNumber: string;
}
interface preferenceTS {
  language: string;
  location: string;
  platformPreferences: { darkMode: boolean };
  timezone: keyof typeof TimeZoneAbbreviations;
}

interface skillTypeTs {
  id: string;
  proficiency: string;
  skillId: string;
  skillTag: string;
  source: string;
  verificationType: string | null;
  verifiedat: string | null;
  verifiedby: string | null;
}
interface nameTS {
  fullName: string;
  lastName: string;
  firstName: string;
}
interface locationTS {
  city: string;
  country: string;
  locationName: string;
  region: string;
  workingLocation: string;
}
interface GoalTs {
  objective: string;
  description: string;
}
interface employmentTS {
  assignmentStatus: string;
  band: string;
  bg: string;
  clientName: string;
  designation: string;
  du: string;
  grade: string;
  operatingUnit: string;
  organization: string;
  role: string;
  title: string;
}

interface BillingTs {
  billingBg: string;
  billingRole: string;
  billingRoleEndDate: string;
  billingRoleStartDate: string;
}

const INIT_STATE: IS = {
  profile: {
    billing: {
      billingBg: "",
      billingRole: "",
      billingRoleEndDate: "",
      billingRoleStartDate: ""
    },
    email: "",
    employeeNumber: "",
    enabled: true,
    employment: {
      assignmentStatus: "",
      band: "",
      bg: "",
      clientName: "",
      designation: "",
      du: "",
      grade: "",
      operatingUnit: "",
      organization: "",
      role: "",
      title: ""
    },
    goalInformation: {
      objective: "",
      description: ""
    },
    hasAssessmentRecOrgs: false,
    id: "",
    isManager: false,
    isSuperAdmin: false,
    location: {
      city: "",
      country: "",
      locationName: "",
      region: "",
      workingLocation: ""
    },
    name: {
      fullName: "",
      lastName: "",
      firstName: ""
    },
    onboarding: true,
    org: import.meta.env.VITE_DEFAULT_ORG || "gl-india",
    preferences: {
      language: "",
      location: "",
      platformPreferences: { darkMode: false },
      timezone: "GMT"
    },
    profileImageUrl: "",
    project: {
      allocationEndDate: "",
      allocationStartDate: "",
      projectManager: "",
      projectName: "",
      projectNumber: ""
    },
    role: [],
    skillTag: [],
    subscriberIdNovu: "",
    summary: "",
    supervisor: "",
    totalExperience: 0,
    unAuthorized: false,
    reportees: []
  },
  complyToLatestTOS: false,
  tosVersionId: null,
  tosAcceptanceDate: null,
  visitedOnboarding: false,
  org: import.meta.env.VITE_DEFAULT_ORG || "gl-india",
  token: null,
  firstTimeLogin: false,
  orgRoutes: {}
};

const UserInfoSlice = createSlice({
  name: "UserInfoSlice",
  initialState: INIT_STATE,
  reducers: {
    setUser(state, action) {
      state.profile = action.payload;
    },
    clearUser(state) {
      state.profile = INIT_STATE.profile;
    },
    updateTOS(state) {
      state.complyToLatestTOS = true;
    },
    updateTOSWithData(state, action) {
      state.tosVersionId = action.payload.tosVersionId;
      state.tosAcceptanceDate = action.payload.tosAcceptanceDate;
    },
    updateTOSVersion(state, action) {
      state.tosVersionId = action.payload.tosVersionId;
      state.tosAcceptanceDate = action.payload.tosAcceptanceDate;
    },
    setVisitedOnboarding(state) {
      state.visitedOnboarding = true;
    },
    setOrg(state, action) {
      state.org = import.meta.env.VITE_DEFAULT_ORG || action.payload;
    },
    setToken: (state, action) => {
      state.token = action.payload;
    },
    clearToken: (state) => {
      state.token = null;
    },
    setFirstTimeLogin: (state, action) => {
      state.firstTimeLogin = action.payload;
    },
    setOrgRoute: (state, action) => {
      state.orgRoutes = action.payload;
    }
  }
});

export default UserInfoSlice;

export const UserInfoSliceActions = UserInfoSlice.actions;
